import React, { useMemo } from 'react';
import { matchPath, useLocation } from '@react-router';
import { createContext } from '@shared/utils/createContext';
import globalWindow from '@shared/core/globals';
import type { AppRoutePath } from './useRouterHelper';

/**
 * Every app mounted through the joy-web router must declare whether it is guest-facing:
 * surfaces a host's guests can see (guest site, registry guest, ecard guest, ...) vs
 * host-only surfaces (admin, card customizer, acquisition funnels, account, ...).
 *
 * The declaration is required on every top-level app route (`AppRoutePath.isGuestFacing`) and is
 * resolved here from the location, ABOVE the router's `Switch`, so very-early-boot code
 * (the full-page loader in `Setup`, which renders as a sibling of the routes) can read it
 * without waiting for the matched app to mount.
 */

/**
 * Whether the current app declared itself guest-facing. Defaults to false when no route
 * declared it (defensive default: never treat an undeclared surface as guest-facing,
 * so Joy branding is never suppressed by accident).
 */
const [Provider, useIsGuestFacingApp] = createContext<boolean>({
  name: 'GuestFacing',
  strict: false,
  initialValue: false,
});

export { useIsGuestFacingApp };

/**
 * Resolve the `isGuestFacing` declaration for a pathname by replaying `Switch`'s
 * first-match-wins order over the same route table the router renders.
 *
 * Unmatched paths resolve to `false` (host-facing): an undeclared surface must never
 * suppress Joy branding by accident.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const resolveIsGuestFacingForPath = (routes: ReadonlyArray<AppRoutePath<any>>, pathname: string): boolean => {
  const matched = routes.find(route => matchPath(pathname, { path: route.path, exact: route.exact }));
  return matched?.isGuestFacing ?? false;
};

type GuestFacingRouteProviderProps = Readonly<{
  /** Must be a stable reference — the resolution memo keys on it. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  routes: ReadonlyArray<AppRoutePath<any>>;
}>;

/**
 * Provides the current route's `isGuestFacing` declaration. Must be mounted above `Setup` so
 * the full-page loader is inside it — the loader is a sibling of the routes, so a provider
 * mounted per matched route would never reach it.
 */
export const GuestFacingRouteProvider: React.FC<GuestFacingRouteProviderProps> = ({ routes, children }) => {
  const { pathname } = useLocation();
  const isGuestFacing = useMemo(() => resolveIsGuestFacingForPath(routes, pathname), [routes, pathname]);
  return <Provider value={isGuestFacing}>{children}</Provider>;
};

/**
 * Render contexts `constructPreviewUrl` (WebsitePreview.utils.ts) sets on admin preview frames.
 * Kept in sync with that builder — a value it stops emitting silently drops a preview back to
 * guest branding, and a value added here widens what counts as host-viewed.
 */
const ADMIN_PREVIEW_CONTEXTS: ReadonlySet<string> = new Set(['websiteDesigner', 'websiteEditor', 'adminGuestSitePreview']);

/**
 * Whether the current page is an admin iframe preview of a guest-facing surface. The host is the
 * viewer, so Joy branding applies to chrome like the loader even though the route declares itself
 * guest-facing.
 *
 * `?preview=true` alone is not enough: it is public, so a guest could append it to a white-label
 * event URL and pull the Joy logo back onto a page the customer paid to de-brand. All three of the
 * preview contract's markers must hold — the param, a known `ctx`, and actually being framed.
 */
export const useIsInAdminPreviewIframe = (): boolean => {
  const { search } = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(search);
    if (params.get('preview') !== 'true' || !ADMIN_PREVIEW_CONTEXTS.has(params.get('ctx') ?? '')) {
      return false;
    }
    // Framed check runs last: it is the marker a URL alone cannot forge.
    return __BROWSER__ && globalWindow.self !== globalWindow.top;
  }, [search]);
};
